import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Building2, ChevronDown, X } from "lucide-react";
import { sidebarSections } from "../../util/sidebarLinks";
import { useTenantBranding } from "../../hooks/useTenantBranding";
import TechFrigateLogo from "../Common/TechFrigateLogo";

const itemBase = "group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors";
const itemIdle = "text-slate-300 hover:bg-white/5 hover:text-white";
const itemActive = "bg-brand-500 text-white shadow-sm shadow-brand-900/40";

const groupForPath = (path) =>
  sidebarSections
    .flatMap((s) => s.links)
    .find((l) => l.subLink?.some((s) => s.route.toLowerCase() === path.toLowerCase()))?.name;

export const Sidebar = ({ setShowForm, isOpen, onClose }) => {
  const { image, name } = useTenantBranding();
  const { pathname } = useLocation();
  const sidebarRef = useRef(null);
  const [openGroup, setOpenGroup] = useState(() => groupForPath(pathname));

  useEffect(() => {
    const group = groupForPath(pathname);
    if (group) setOpenGroup(group);
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const onNavigate = () => {
    setShowForm(false);
    if (window.innerWidth < 1024) onClose();
  };

  return (
    <aside
      ref={sidebarRef}
      className={`fixed inset-y-0 left-0 z-50 w-64 shrink-0 transition-transform duration-300 lg:static lg:z-0 lg:translate-x-0 ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="flex h-full flex-col bg-navy-900">
        <div className="flex h-16 items-center justify-between gap-3 border-b border-white/10 px-5">
          <div className="flex min-w-0 items-center gap-3">
            {image ? (
              <img src={image} alt="" className="h-9 w-9 shrink-0 rounded-lg bg-white object-cover ring-1 ring-white/10" />
            ) : (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-500 text-white">
                <Building2 className="h-5 w-5" />
              </span>
            )}
            <div className="min-w-0 leading-tight">
              <p className="truncate text-sm font-semibold text-white">{name || "Hospital"}</p>
              <p className="text-xs text-slate-400">Hospital admin portal</p>
            </div>
          </div>
          <button className="rounded-md p-1 text-slate-400 hover:bg-white/10 lg:hidden" onClick={onClose} aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="customScrollbar flex-1 space-y-6 overflow-y-auto px-3 py-5">
          {sidebarSections.map((section) => (
            <div key={section.title}>
              <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{section.title}</p>
              <div className="space-y-0.5">
                {section.links.map((link) => {
                  const Icon = link.icon;
                  if (!link.subLink) {
                    return (
                      <NavLink
                        key={link.route}
                        to={link.route}
                        onClick={onNavigate}
                        className={({ isActive }) => `${itemBase} ${isActive ? itemActive : itemIdle}`}
                      >
                        <Icon className="h-[18px] w-[18px] shrink-0" />
                        {link.name}
                      </NavLink>
                    );
                  }
                  const expanded = openGroup === link.name;
                  const childActive = groupForPath(pathname) === link.name;
                  return (
                    <div key={link.name}>
                      <button
                        type="button"
                        onClick={() => setOpenGroup(expanded ? null : link.name)}
                        className={`${itemBase} ${childActive ? "text-white" : itemIdle}`}
                        aria-expanded={expanded}
                      >
                        <Icon className={`h-[18px] w-[18px] shrink-0 ${childActive ? "text-brand-300" : ""}`} />
                        <span className="flex-1 text-left">{link.name}</span>
                        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${expanded ? "rotate-180" : ""}`} />
                      </button>
                      {expanded && (
                        <div className="ml-[22px] mt-0.5 space-y-0.5 border-l border-white/10 pl-3">
                          {link.subLink.map((sub) => (
                            <NavLink
                              key={sub.route}
                              to={sub.route}
                              onClick={onNavigate}
                              className={({ isActive }) =>
                                `block rounded-md px-3 py-1.5 text-sm transition-colors ${
                                  isActive ? "bg-white/10 font-medium text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
                                }`
                              }
                            >
                              {sub.name}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-white/10 px-3 py-3 [&_*]:!text-slate-400">
          <TechFrigateLogo />
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
